import { ProfileSkeleton } from './Skeleton';
import { SkeletonList } from './SkeletonList';
import { SkeletonChat } from './SkeletonChat';
import { SkeletonForm } from './SkeletonForm';
import { SkeletonDashboard } from './SkeletonDashboard';

/**
 * PageSkeleton — route-aware Tier-1 loading shape.
 *
 * Dispatched by LazyShell / UserProvider at route boundaries so the
 * Suspense fallback mirrors the destination's layout. Keep this a flat
 * switch — no logic, just composition of the existing compound
 * primitives. Adding a new route key adds a new case.
 */

export type PageSkeletonRoute =
    | 'home'
    | 'networking'
    | 'messaging'
    | 'education'
    | 'mentorship'
    | 'profile'
    | 'settings'
    | 'admin'
    | 'role-onboarding'
    | 'generic';

export type PageSkeletonProps = {
    route?: PageSkeletonRoute;
    className?: string;
};

export function PageSkeleton({ route = 'generic', className = '' }: PageSkeletonProps) {
    return (
        <div
            className={`page-skeleton page-skeleton--${route} ${className}`}
            role="status"
            aria-busy="true"
            aria-label={`Loading ${route === 'generic' ? 'page' : route}`}
        >
            {/* Header strip — mirrors SurfaceHeader.normalize chrome so the
             *  loading state doesn't flash a solid-bg viewport before the
             *  real `.profile-header` / `.chatroom-header` / `.surface-header`
             *  drops in. Matches --header-height, --bg-canvas, border-bottom. */}
            <div className="page-skeleton__header" aria-hidden="true">
                <div className="skeleton page-skeleton__header-avatar" />
                <div className="skeleton page-skeleton__header-title" />
            </div>
            <div className="page-skeleton__body">
                {renderForRoute(route)}
            </div>
        </div>
    );
}

function renderForRoute(route: PageSkeletonRoute) {
    switch (route) {
        case 'home':
            return <SkeletonDashboard metricCount={4} hasChart={false} hasFeed />;
        case 'admin':
            return <SkeletonDashboard metricCount={4} hasChart hasFeed />;
        case 'networking':
        case 'education':
            return <SkeletonList count={6} item="card" gap="md" />;
        case 'messaging':
            return <SkeletonChat messages={5} />;
        case 'mentorship':
            return <SkeletonList count={4} item="row" gap="md" />;
        case 'profile':
            return <ProfileSkeleton fields={5} />;
        case 'settings':
            return <SkeletonForm fields={5} hasActions />;
        case 'role-onboarding':
            return <SkeletonForm fields={3} hasActions />;
        case 'generic':
        default:
            return <SkeletonList count={4} item="row" gap="md" />;
    }
}
